// src/hooks/useHomeData.ts
import { useCallback, useMemo, useState } from "react";
import { api, type AgentJobItem, type CharacterData, type UserData } from "../lib/api";
import { getGoogleConsentStatus, type GoogleConsentScopeStatus } from "../lib/googleConsentStatus";
import { getByPath, pickStringByPaths } from "../lib/path";
import { normalizeUserToHomeVM, type HomeViewModel } from "../lib/normalizeHomeUser";
import { resolveBackendAssetUrl } from "../lib/backendAsset";
import {
  normalizeGoogleIntegrationStatus,
  type GoogleIntegrationView,
} from "../lib/googleIntegration";
import {
  GOOGLE_SCOPE_BUNDLES,
  type GoogleScopeBundle,
} from "../lib/googleScopes";
import {
  readLineLinkSnapshot,
  resolveLineLinkSnapshotFromApiStatus,
  writeLineLinkSnapshot,
  type LineLinkSnapshot,
} from "../lib/lineLink";
import { resolveRememberedNewsJobMode } from "../lib/newsJob";
import { readLastSeenMemoId, writeLastSeenMemoId } from "../lib/memoSeen";
import { storage } from "../lib/storage";
import { getDeviceParams } from "../lib/device";
import { AppError, logError, toUserMessage } from "../lib/errors";

export type HomeDebugEntry = {
  time: string;
  phase: string;
  note?: string;
  extra?: any;
};

export type HomeActivityLog = {
  id: string;
  text: string;
  time: string;
};

export type HomeGoogleIntegrationViews = Record<GoogleScopeBundle, GoogleIntegrationView>;

function createLoadingGoogleIntegrationView(): GoogleIntegrationView {
  return {
    state: "loading",
    scopeBundleGranted: false,
    missingScopes: [],
  };
}

function createErrorGoogleIntegrationView(): GoogleIntegrationView {
  return {
    state: "error",
    scopeBundleGranted: false,
    missingScopes: [],
  };
}

function toGoogleIntegrationView(
  scopeBundle: GoogleScopeBundle,
  status: GoogleConsentScopeStatus
): GoogleIntegrationView {
  return normalizeGoogleIntegrationStatus({
    connectionStatus: status.connectionStatus,
    isConnected: status.connectionStatus.trim().toLowerCase() !== "not_connected",
    scopeBundle,
    scopeBundleGranted: status.granted,
    missingScopes: status.missingScopes,
  });
}

function normalizeId(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

export function resolveHomeCurrentCharacterId(
  user: UserData,
  fallbackCharacterId?: string | null
): string | null {
  return (
    normalizeId(
      getByPath(user, "currentCharacter.characterId") ??
        getByPath(user, "currentCharacter.character_id") ??
        getByPath(user, "data.currentCharacter.characterId") ??
        getByPath(user, "data.currentCharacter.character_id") ??
        getByPath(user, "currentCharacter.id") ??
        getByPath(user, "data.currentCharacter.id") ??
        getByPath(user, "currentCharacterId") ??
        getByPath(user, "data.currentCharacterId")
    ) ?? normalizeId(fallbackCharacterId)
  );
}

function resolveCharacterContent(user: UserData, characters: CharacterData[]) {
  const currentCharacterId = resolveHomeCurrentCharacterId(user);

  const currentCharacterName = pickStringByPaths(user, [
    "currentCharacter.characterName",
    "data.currentCharacter.characterName",
    "currentCharacter.name",
    "data.currentCharacter.name",
  ]);

  const currentCharacter =
    characters.find((character) => {
      const characterId = normalizeId(character.characterId ?? character.id);
      return !!currentCharacterId && characterId === currentCharacterId;
    }) ??
    characters.find((character) => {
      const name = pickStringByPaths(character, ["characterName", "name"]);
      return !!currentCharacterName && name === currentCharacterName;
    }) ??
    null;

  return {
    messageToUser: pickStringByPaths(currentCharacter, ["messageToUser"]) ?? "",
    reportMemoToUser: pickStringByPaths(currentCharacter, ["reportMemoToUser"]) ?? "",
  };
}

function nowIso() {
  return new Date().toISOString();
}

function toHHMM(value: unknown): string {
  if (typeof value === "string" && /^\d{1,2}:\d{2}$/.test(value.trim())) {
    return value.trim();
  }
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${hh}:${mm}`;
    }
  }
  return "--:--";
}

function toLocalDateString(isoString: string): string {
  const d = new Date(isoString);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function resolveNewsModeLabel(item: AgentJobItem): "ニュース" | "レポート" {
  const parsedMeta = (() => {
    if (!item.meta) return null;
    if (typeof item.meta === "object") return item.meta;
    if (typeof item.meta !== "string") return null;
    try {
      const parsed = JSON.parse(item.meta);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  })();

  const modeCandidate =
    pickStringByPaths(item, [
      "mode",
      "newsMode",
      "news_mode",
      "payload.mode",
      "request.mode",
      "requestPayload.mode",
      "meta.mode",
      "meta.newsMode",
      "meta.news_mode",
      "meta.news.mode",
      "meta.request.mode",
      "meta.requestPayload.mode",
    ]) ??
    pickStringByPaths(parsedMeta, [
      "mode",
      "newsMode",
      "news_mode",
      "news.mode",
      "request.mode",
      "requestPayload.mode",
    ]) ??
    "";

  const normalizedMode = modeCandidate.trim().toLowerCase();
  if (normalizedMode === "report") return "レポート";
  if (normalizedMode === "news") return "ニュース";

  const summary = (item.summary ?? "").trim().toLowerCase();
  if (summary.includes("レポート") || summary.includes("report")) {
    return "レポート";
  }

  const rememberedMode = resolveRememberedNewsJobMode(item.jobId);
  if (rememberedMode === "report") {
    return "レポート";
  }

  return "ニュース";
}

export function buildAgentJobTitle(resource: string, status: string, newsModeLabel: "ニュース" | "レポート" = "ニュース"): string {
  if (resource === "receipt") {
    if (status === "completed") return "領収書の処理が完了しました";
    if (status === "failed") return "領収書の処理に失敗しました";
    if (status === "in_progress") return "領収書を処理中です";
    return "領収書の処理を受け付けました";
  }
  if (resource === "news") {
    if (status === "completed") return `${newsModeLabel}送信の処理が完了しました`;
    if (status === "failed") return `${newsModeLabel}送信の処理に失敗しました`;
    if (status === "in_progress") return `${newsModeLabel}を送信中です`;
    return `${newsModeLabel}送信の処理を受け付けました`;
  }
  if (status === "completed") return "処理が完了しました";
  if (status === "failed") return "処理に失敗しました";
  if (status === "in_progress") return "処理中です";
  return "処理を受け付けました";
}

function agentJobItemToActivityLog(item: AgentJobItem, idx: number): HomeActivityLog {
  const newsModeLabel = item.resource === "news" ? resolveNewsModeLabel(item) : "ニュース";
  const title = buildAgentJobTitle(item.resource, item.status, newsModeLabel);
  const detail = item.summary?.trim() || item.errorMessage?.trim() || null;
  const text = detail ? `${title}\n${detail}` : title;
  return {
    id: item.id ?? `job-${idx}`,
    text,
    time: toHHMM(item.processedAt ?? item.updatedAt ?? item.requestedAt),
  };
}

function toActivityLog(item: any, idx: number): HomeActivityLog | null {
  if (typeof item === "string") {
    const text = item.trim();
    if (!text) return null;
    return { id: `text-${idx}`, text, time: "--:--" };
  }

  if (!item || typeof item !== "object") return null;

  const textCandidate =
    item.text ?? item.message ?? item.memo ?? item.title ?? item.content ?? item.description;
  const text = typeof textCandidate === "string" ? textCandidate.trim() : "";
  if (!text) return null;

  const idCandidate = item.id ?? item.logId ?? item.historyId;
  const id = idCandidate != null ? String(idCandidate) : `obj-${idx}`;
  const time = toHHMM(item.time ?? item.date ?? item.createdAt ?? item.timestamp ?? item.at);

  return { id, text, time };
}

function extractActivityLogs(user: UserData): HomeActivityLog[] {
  const candidates = [
    "activityLogs",
    "activities",
    "logs",
    "history",
    "reportMemos",
    "reports",
    "data.activityLogs",
    "data.activities",
    "data.logs",
    "data.history",
    "data.reportMemos",
    "data.reports",
  ];

  for (const path of candidates) {
    const list = getByPath(user, path);
    if (!Array.isArray(list)) continue;

    const mapped = list.map((item, idx) => toActivityLog(item, idx)).filter(Boolean) as HomeActivityLog[];
    if (mapped.length > 0) return mapped;
  }

  return [];
}

export function useHomeData() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vm, setVm] = useState<HomeViewModel | null>(null);
  const [currentCharacterId, setCurrentCharacterId] = useState<string | null>(null);
  const [modelUrl, setModelUrl] = useState<string | null>(null);
  const [activityLogs, setActivityLogs] = useState<HomeActivityLog[]>([]);
  const [todayJobLogs, setTodayJobLogs] = useState<HomeActivityLog[]>([]);
  const [yesterdayJobLogs, setYesterdayJobLogs] = useState<HomeActivityLog[]>([]);
  const [messageToUser, setMessageToUser] = useState("");
  const [lineLinkSnapshot, setLineLinkSnapshot] = useState<LineLinkSnapshot>(readLineLinkSnapshot());
  const [googleIntegrationViews, setGoogleIntegrationViews] = useState<HomeGoogleIntegrationViews>({
    [GOOGLE_SCOPE_BUNDLES.receipt]: createLoadingGoogleIntegrationView(),
    [GOOGLE_SCOPE_BUNDLES.news]: createLoadingGoogleIntegrationView(),
    [GOOGLE_SCOPE_BUNDLES.bundle]: createLoadingGoogleIntegrationView(),
  });
  const [debug, setDebug] = useState<HomeDebugEntry[]>([]);
  const [hasNewMemo, setHasNewMemo] = useState(false);

  const pushDebug = useCallback((phase: string, note?: string, extra?: any) => {
    setDebug((prev) => [...prev, { time: nowIso(), phase, note, extra }]);
  }, []);

  const load = useCallback(async () => {
    let alive = true;
    const lastSeenMemoId = readLastSeenMemoId();
    const auth = storage.getAuth();

    setLoading(true);
    setError(null);
    setVm(null);
    setModelUrl(null);
    setActivityLogs([]);
    setTodayJobLogs([]);
    setYesterdayJobLogs([]);
    setMessageToUser("");

    pushDebug("mount", "Home load start", {
      auth: { userId: auth.userId },
      device: getDeviceParams(),
    });

    try {
      pushDebug("calling_getUser");
      const user: UserData = await api.getUser();
      if (!alive) return;

      pushDebug("getUser_success", undefined, { user });

      try {
        const lineLinkStatus = await api.getLineLinkStatus();
        const nextLineLinkSnapshot = resolveLineLinkSnapshotFromApiStatus(lineLinkStatus, readLineLinkSnapshot());
        setLineLinkSnapshot(nextLineLinkSnapshot);
        writeLineLinkSnapshot(nextLineLinkSnapshot);
      } catch (lineLinkError) {
        logError(lineLinkError);
        setLineLinkSnapshot(readLineLinkSnapshot());
        pushDebug("line_link_status_error", "GET /line/status failed", { err: lineLinkError });
      }

      try {
        const googleConsentStatus = await getGoogleConsentStatus();
        const nextGoogleIntegrationViews: HomeGoogleIntegrationViews = {
          [GOOGLE_SCOPE_BUNDLES.receipt]: toGoogleIntegrationView(
            GOOGLE_SCOPE_BUNDLES.receipt,
            googleConsentStatus.receipt
          ),
          [GOOGLE_SCOPE_BUNDLES.news]: toGoogleIntegrationView(
            GOOGLE_SCOPE_BUNDLES.news,
            googleConsentStatus.news
          ),
          [GOOGLE_SCOPE_BUNDLES.bundle]: toGoogleIntegrationView(
            GOOGLE_SCOPE_BUNDLES.bundle,
            googleConsentStatus.bundle
          ),
        };

        setGoogleIntegrationViews(nextGoogleIntegrationViews);
      } catch (googleStatusError) {
        logError(googleStatusError);
        pushDebug("google_integration_status_error", "GET /integrations/google/status failed", {
          err: googleStatusError,
        });
        setGoogleIntegrationViews({
          [GOOGLE_SCOPE_BUNDLES.receipt]: createErrorGoogleIntegrationView(),
          [GOOGLE_SCOPE_BUNDLES.news]: createErrorGoogleIntegrationView(),
          [GOOGLE_SCOPE_BUNDLES.bundle]: createErrorGoogleIntegrationView(),
        });
      }

      // storage: currentCharacterId / currentVoiceId を保存（他画面向け）
      const resolvedCurrentCharacterId = resolveHomeCurrentCharacterId(user, auth.currentCharacterId);

      const currentVoiceId =
        getByPath(user, "currentVoice.id") ?? getByPath(user, "data.currentVoice.id");

      try {
        (storage as any).setAuth({
          currentCharacterId: resolvedCurrentCharacterId ?? undefined,
          currentVoiceId: currentVoiceId != null ? String(currentVoiceId) : undefined,
        });
      } catch {
        // noop
      }

      if (alive) {
        setCurrentCharacterId(resolvedCurrentCharacterId);
      }

      // 3Dモデルプリロード機能は一時的に無効化
      // model resolve（見つからない場合は null）
      // pushDebug("model_resolve_start");
      // const resolved = await resolveCharacterModelUrl({
      //   user,
      //   getCharacterList: api.getCharacterList,
      // });
      // if (!alive) return;
      //
      // setModelUrl(resolved.modelUrl);
      // pushDebug("model_resolve_done", undefined, resolved);

      const normalized = normalizeUserToHomeVM(user);
      setVm(normalized);
      setActivityLogs(extractActivityLogs(user));

      let characterContent = { messageToUser: "", reportMemoToUser: "" };
      try {
        pushDebug("calling_getCharacterList");
        const characters = await api.getCharacterList();
        if (!alive) return;
        characterContent = resolveCharacterContent(user, characters);

        // キャラクター一覧から現在のキャラクターの画像URLを取得してVMに反映
        const currentCharId = resolvedCurrentCharacterId;
        const currentChar =
          characters.find((c) => {
            const id = normalizeId(c.characterId ?? c.id);
            return !!currentCharId && id === currentCharId;
          }) ??
          characters.find((c) => c.isCurrent === true) ??
          null;

        if (currentChar) {
          const imageRaw = currentChar.imageUrl ?? currentChar.image;
          const iconRaw = currentChar.iconImageUrl ?? currentChar.iconImage;
          setVm((prev) => {
            if (!prev) return prev;
            const nextImageUrl = resolveBackendAssetUrl(imageRaw) ?? prev.imageUrl;
            const nextIconUrl = resolveBackendAssetUrl(iconRaw) ?? prev.iconUrl;
            if (nextImageUrl === prev.imageUrl && nextIconUrl === prev.iconUrl) return prev;
            return { ...prev, imageUrl: nextImageUrl, iconUrl: nextIconUrl };
          });
        }
      } catch (characterError) {
        logError(characterError);
        pushDebug("getCharacterList_error", "GET /character failed", { err: characterError });
      }

      setMessageToUser(characterContent.messageToUser);

      try {
        const agentJobsData = await api.getAgentJobs({ limit: 50 });
        const today = toLocalDateString(new Date().toISOString());
        const yesterday = toLocalDateString(new Date(Date.now() - 86400000).toISOString());

        const todayLogs: HomeActivityLog[] = [];
        const yestLogs: HomeActivityLog[] = [];

        (agentJobsData.items ?? []).forEach((item, idx) => {
          const dateStr = toLocalDateString(item.requestedAt);
          const log = agentJobItemToActivityLog(item, idx);
          if (dateStr === today) todayLogs.push(log);
          else if (dateStr === yesterday) yestLogs.push(log);
        });

        setTodayJobLogs(todayLogs);
        setYesterdayJobLogs(yestLogs);

        const latestMemoId = todayLogs[0]?.id ?? yestLogs[0]?.id ?? null;
        const hasNew = latestMemoId !== null && latestMemoId !== lastSeenMemoId;
        setHasNewMemo(hasNew);
        if (latestMemoId) writeLastSeenMemoId(latestMemoId);
      } catch (agentJobsError) {
        logError(agentJobsError);
        pushDebug("agent_jobs_error", "GET /agent/jobs failed", { err: agentJobsError });
      }

      pushDebug("normalize_done", normalized ? `OK: ${normalized.characterName}` : "NULL", {
        characterContent,
      });
    } catch (e) {
      if (e instanceof AppError && e.code === "SESSION_EXPIRED") {
        window.location.replace("/splash");
        return;
      }
      logError(e);
      const msg = toUserMessage(e);
      setError(msg);
      setVm(null);
      setModelUrl(null);
      setActivityLogs([]);
      setTodayJobLogs([]);
      setYesterdayJobLogs([]);
      setMessageToUser("");
      pushDebug("error", msg, { err: e });
    } finally {
      if (!alive) return;
      setLoading(false);
      pushDebug("done");
    }

    return () => {
      alive = false;
    };
  }, [pushDebug]);

  // 初回ロード用に外から呼べる形にし、Home.tsx 側で useEffect する
  const refresh = useCallback(() => {
    void load();
  }, [load]);

  const intimacyPercent = useMemo(() => {
    if (!vm) return 0;
    if (!vm.nextRankUpPoint || vm.nextRankUpPoint <= 0) return 0;
    const p = Math.round((vm.rankUpPoint / vm.nextRankUpPoint) * 100);
    return Math.max(0, Math.min(100, p));
  }, [vm]);

  return {
    loading,
    error,
    vm,
    currentCharacterId,
    modelUrl,
    activityLogs,
    todayJobLogs,
    yesterdayJobLogs,
    messageToUser,
    lineLinkSnapshot,
    googleIntegrationViews,
    googleIntegrationView: googleIntegrationViews[GOOGLE_SCOPE_BUNDLES.receipt],
    debug,
    intimacyPercent,
    hasNewMemo,
    refresh,
  };
}
